// src/stores/campaignStore.js
import { create } from 'zustand';
import * as campaignService from '../services/campaignService';
import useNotificationStore from './notificationStore';

const useCampaignStore = create((set, get) => ({
  // State
  campaigns: [],
  totalCampaigns: 0,
  currentCampaign: null,
  isLoading: false,
  error: null,
  meta: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1
  },
  
  // Actions
  fetchCampaigns: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.getCampaigns(params);
      
      // Handle response structure
      const campaigns = response.data?.campaigns || response.campaigns || [];
      const total = response.data?.totalCount || response.data?.total || campaigns.length;
      
      const meta = {
        total: total,
        page: params.page || 1,
        limit: params.limit || 20,
        totalPages: Math.ceil(total / (params.limit || 20))
      };
      
      set({ 
        campaigns,
        totalCampaigns: total,
        meta,
        isLoading: false 
      });
      
      return { campaigns, meta };
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch campaigns'
      );
      set({ 
        campaigns: [],
        isLoading: false, 
        error: error.message 
      });
      return { campaigns: [], meta: get().meta };
    }
  },
  
  fetchCampaign: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.getCampaignById(id);
      const campaign = response.data?.campaign || response.campaign || response;
      
      set({ 
        currentCampaign: campaign, 
        isLoading: false 
      });
      
      return campaign;
    } catch (error) {
      console.error('Error fetching campaign:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch campaign details'
      );
      set({ 
        currentCampaign: null,
        isLoading: false, 
        error: error.message 
      });
      throw error; // Re-throw for detail pages
    }
  },
  
  createCampaign: async (campaignData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.createCampaign(campaignData);
      const campaign = response.data?.campaign || response.campaign || response;
      
      // Add to campaigns list
      set(state => ({ 
        campaigns: [campaign, ...state.campaigns],
        isLoading: false 
      }));
      
      useNotificationStore.getState().showSuccess('Campaign created successfully');
      return campaign;
    } catch (error) {
      console.error('Error creating campaign:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to create campaign'
      );
      set({ 
        isLoading: false, 
        error: error.message 
      });
      throw error; // Re-throw for form handling
    }
  },
  
  updateCampaign: async (id, campaignData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.updateCampaign(id, campaignData);
      const campaign = response.data?.campaign || response.campaign || response;
      
      // Update in campaigns list
      set(state => ({ 
        campaigns: state.campaigns.map(c => 
          c._id === id ? campaign : c
        ),
        currentCampaign: state.currentCampaign?._id === id ? campaign : state.currentCampaign,
        isLoading: false 
      }));
      
      useNotificationStore.getState().showSuccess('Campaign updated successfully');
      return campaign;
    } catch (error) {
      console.error('Error updating campaign:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to update campaign'
      );
      set({ 
        isLoading: false, 
        error: error.message 
      });
      throw error; // Re-throw for form handling
    }
  },
  
  deleteCampaign: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await campaignService.deleteCampaign(id);
      
      // Remove from campaigns list
      set(state => ({
        campaigns: state.campaigns.filter(campaign => campaign._id !== id),
        currentCampaign: state.currentCampaign?._id === id ? null : state.currentCampaign,
        isLoading: false
      }));
      
      useNotificationStore.getState().showSuccess('Campaign deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting campaign:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to delete campaign'
      );
      set({ 
        isLoading: false, 
        error: error.message 
      });
      throw error; // Re-throw for deletion handling
    }
  },
  
  fetchBrandCampaigns: async (brandId, params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.getBrandCampaigns(brandId, params);
      
      // Handle response structure
      const campaigns = response.data?.campaigns || response.campaigns || [];
      const total = response.data?.totalCount || response.data?.total || campaigns.length;
      
      const meta = {
        total: total,
        page: params.page || 1,
        limit: params.limit || 20,
        totalPages: Math.ceil(total / (params.limit || 20))
      };
      
      set({ 
        campaigns,
        totalCampaigns: total,
        meta,
        isLoading: false 
      });
      
      return { campaigns, meta };
    } catch (error) {
      console.error('Error fetching brand campaigns:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch brand campaigns'
      );
      set({ 
        campaigns: [],
        isLoading: false, 
        error: error.message 
      });
      return { campaigns: [], meta: get().meta };
    }
  },
  
  fetchClientCampaigns: async (clientId, params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.getClientCampaigns(clientId, params);
      
      // Handle response structure
      const campaigns = response.data?.campaigns || response.campaigns || [];
      const total = response.data?.totalCount || response.data?.total || campaigns.length;
      
      const meta = {
        total: total,
        page: params.page || 1,
        limit: params.limit || 20,
        totalPages: Math.ceil(total / (params.limit || 20))
      };
      
      set({ 
        campaigns,
        totalCampaigns: total,
        meta,
        isLoading: false 
      });
      
      return { campaigns, meta };
    } catch (error) {
      console.error('Error fetching client campaigns:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch client campaigns'
      );
      set({ 
        campaigns: [],
        isLoading: false, 
        error: error.message 
      });
      return { campaigns: [], meta: get().meta };
    }
  },
  
  uploadCampaignAsset: async (campaignId, file) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.uploadCampaignAsset(campaignId, file);
      const asset = response.data?.asset || response.asset || response;
      
      // Update current campaign assets if it's loaded
      if (get().currentCampaign && get().currentCampaign._id === campaignId) {
        const currentCampaign = {...get().currentCampaign};
        currentCampaign.assets = [...(currentCampaign.assets || []), asset];
        set({ currentCampaign });
      }
      
      set({ isLoading: false });
      useNotificationStore.getState().showSuccess('Asset uploaded successfully');
      return asset;
    } catch (error) {
      console.error('Error uploading asset:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to upload asset'
      );
      set({ 
        isLoading: false, 
        error: error.message 
      });
      throw error; // Re-throw for upload handling
    }
  },
  
  addCampaignMilestone: async (campaignId, milestone) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.addCampaignMilestone(campaignId, milestone);
      const newMilestone = response.data?.milestone || response.milestone || response;
      
      // Update current campaign milestones if it's loaded
      if (get().currentCampaign && get().currentCampaign._id === campaignId) {
        const currentCampaign = {...get().currentCampaign};
        currentCampaign.milestones = [...(currentCampaign.milestones || []), newMilestone];
        set({ currentCampaign });
      }
      
      set({ isLoading: false });
      useNotificationStore.getState().showSuccess('Milestone added successfully');
      return newMilestone;
    } catch (error) {
      console.error('Error adding milestone:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to add milestone'
      );
      set({ 
        isLoading: false, 
        error: error.message 
      });
      throw error; // Re-throw for milestone handling
    }
  },
  
  updateCampaignMilestone: async (campaignId, milestoneId, updates) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.updateCampaignMilestone(campaignId, milestoneId, updates);
      const updatedMilestone = response.data?.milestone || response.milestone || response;
      
      // Update current campaign milestone if it's loaded
      if (get().currentCampaign && get().currentCampaign._id === campaignId) {
        const currentCampaign = {...get().currentCampaign};
        const milestoneIndex = currentCampaign.milestones.findIndex(m => m._id === milestoneId);
        if (milestoneIndex !== -1) {
          currentCampaign.milestones[milestoneIndex] = updatedMilestone;
          set({ currentCampaign });
        }
      }
      
      set({ isLoading: false });
      useNotificationStore.getState().showSuccess('Milestone updated successfully');
      return updatedMilestone;
    } catch (error) {
      console.error('Error updating milestone:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to update milestone'
      );
      set({ 
        isLoading: false, 
        error: error.message 
      });
      throw error; // Re-throw for milestone handling
    }
  },
  
  updateCampaignStatus: async (campaignId, status) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.updateCampaignStatus(campaignId, status);
      const campaign = response.data?.campaign || response.campaign || response;
      
      // Update current campaign if it's loaded
      if (get().currentCampaign && get().currentCampaign._id === campaignId) {
        const currentCampaign = {...get().currentCampaign, status};
        set({ currentCampaign });
      }
      
      // Update in campaigns list if it exists there
      set(state => ({
        campaigns: state.campaigns.map(c => 
          c._id === campaignId ? {...c, status} : c
        )
      }));
      
      set({ isLoading: false });
      useNotificationStore.getState().showSuccess('Campaign status updated successfully');
      return campaign;
    } catch (error) {
      console.error('Error updating campaign status:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to update campaign status'
      );
      set({ 
        isLoading: false, 
        error: error.message 
      });
      throw error; // Re-throw for status handling
    }
  },
  
  addBudgetTransaction: async (campaignId, transaction) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.addBudgetTransaction(campaignId, transaction);
      const budget = response.data?.budget || response.budget || response;
      
      // Update current campaign budget if it's loaded
      if (get().currentCampaign && get().currentCampaign._id === campaignId) {
        const currentCampaign = {...get().currentCampaign};
        currentCampaign.budget = budget;
        set({ currentCampaign });
      }
      
      set({ isLoading: false });
      useNotificationStore.getState().showSuccess('Budget transaction added successfully');
      return budget;
    } catch (error) {
      console.error('Error adding budget transaction:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to add budget transaction'
      );
      set({ 
        isLoading: false, 
        error: error.message 
      });
      throw error; // Re-throw for budget handling
    }
  },
  
  updateCampaignKPIs: async (campaignId, kpiUpdates) => {
    set({ isLoading: true, error: null });
    try {
      const response = await campaignService.updateCampaignKPIs(campaignId, kpiUpdates);
      const kpis = response.data?.kpis || response.kpis || response;
      
      // Update current campaign KPIs if it's loaded
      if (get().currentCampaign && get().currentCampaign._id === campaignId) {
        const currentCampaign = {...get().currentCampaign};
        currentCampaign.kpis = kpis;
        set({ currentCampaign });
      }
      
      set({ isLoading: false });
      useNotificationStore.getState().showSuccess('KPIs updated successfully');
      return kpis;
    } catch (error) {
      console.error('Error updating KPIs:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to update KPIs'
      );
      set({ 
        isLoading: false, 
        error: error.message 
      });
      throw error; // Re-throw for KPI handling
    }
  },
  
  clearCurrentCampaign: () => {
    set({ currentCampaign: null });
  },
  
  clearError: () => {
    set({ error: null });
  }
}));

export { useCampaignStore };
export default useCampaignStore;